import React, { useEffect, useState, useMemo } from "react";
import {
  Loader2, Upload, QrCode, FileUp, ExternalLink, Pencil,
  LayoutGrid, List, Search, X, Dumbbell, MapPin, Plus, Trash2, Package, ImageDown,
} from "lucide-react";
import { machinesApi, zonasApi, accesoriosApi, adminApi, formatApiError } from "../../lib/api";
import MachineEditModal from "../../components/MachineEditModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const DIFICULTAD_LABEL = { 1: "Fácil", 2: "Medio", 3: "Avanzado" };
const DIFICULTAD_COLOR = {
  1: "text-green-400 border-green-500/30",
  2: "text-[#FFCC00] border-[#FFCC00]/30",
  3: "text-red-400 border-red-500/30",
};

const NEW_MACHINE_TEMPLATE = { numero: "", nombre: "", planta: null, grupo_muscular: null, tipo_esfuerzo: null, dificultad: null, foto_url: null, video_url: null, instrucciones_breves: null, activa: true, _existing: false };

function fotoSrc(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) return `${BACKEND}${url}`;
  return url;
}

export default function MaquinasTab() {
  const [machines, setMachines] = useState([]);
  const [zones, setZones] = useState([]);
  const [machineCount, setMachineCount] = useState(null);
  const [editingMachine, setEditingMachine] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState("");
  const [qrDownloading, setQrDownloading] = useState(false);
  const [qrBaseUrl, setQrBaseUrl] = useState(() => typeof window !== "undefined" ? window.location.origin : "");
  const [reoptimizing, setReoptimizing] = useState(false);
  const [reoptResult, setReoptResult] = useState(null);
  const [view, setView] = useState("catalog"); // "catalog" | "gestion" | "zonas" | "accesorios"
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Catalog filters
  const [search, setSearch] = useState("");
  const [filterGrupo, setFilterGrupo] = useState("");
  const [filterDif, setFilterDif] = useState("");
  const [filterPlanta, setFilterPlanta] = useState("");

  const load = async () => {
    try {
      const [m, z] = await Promise.all([machinesApi.adminList(), zonasApi.list().catch(() => [])]);
      setMachines(m);
      setZones(z);
      setMachineCount(m.filter((x) => x.activa).length);
    } catch { setMachineCount(0); }
  };

  useEffect(() => { load(); }, []);

  const onImportCsv = async () => {
    if (!importFile) return;
    setImporting(true); setImportError(""); setImportResult(null);
    try {
      const result = await machinesApi.importCsv(importFile);
      setImportResult(result); setImportFile(null); await load();
    } catch (e) { setImportError(formatApiError(e?.response?.data?.detail) || "Error importando el CSV"); }
    finally { setImporting(false); }
  };

  const onDownloadQr = async () => {
    setQrDownloading(true); setImportError("");
    try { await machinesApi.downloadQrSheet(qrBaseUrl); }
    catch (e) { setImportError(e?.message || "Error generando el PDF"); }
    finally { setQrDownloading(false); }
  };

  const grupos = useMemo(() => {
    const all = new Set();
    machines.forEach((m) => {
      if (m.grupo_muscular) {
        m.grupo_muscular.split(/[·,\/]/).forEach((g) => { const t = g.trim(); if (t) all.add(t); });
      }
    });
    return [...all].sort();
  }, [machines]);
  const plantas = useMemo(() => [...new Set(machines.map((m) => m.planta).filter(Boolean))].sort(), [machines]);

  const filtered = useMemo(() => {
    return machines.filter((m) => {
      if (search) {
        const q = search.toLowerCase();
        if (!m.nombre?.toLowerCase().includes(q) && !m.numero?.toLowerCase().includes(q)) return false;
      }
      if (filterGrupo && !(m.grupo_muscular || "").toLowerCase().includes(filterGrupo.toLowerCase())) return false;
      if (filterDif && String(m.dificultad) !== filterDif) return false;
      if (filterPlanta && m.planta !== filterPlanta) return false;
      return true;
    });
  }, [machines, search, filterGrupo, filterDif, filterPlanta]);

  const hasFilters = search || filterGrupo || filterDif || filterPlanta;
  const clearFilters = () => { setSearch(""); setFilterGrupo(""); setFilterDif(""); setFilterPlanta(""); };

  const handleSaved = (updated) => {
    setMachines((prev) => {
      const exists = prev.find((m) => m.numero === updated.numero);
      if (exists) return prev.map((m) => m.numero === updated.numero ? updated : m);
      return [...prev, updated].sort((a, b) => a.numero.localeCompare(b.numero));
    });
    setMachineCount((prev) => {
      const list = machines.find((m) => m.numero === updated.numero)
        ? machines.map((m) => m.numero === updated.numero ? updated : m)
        : [...machines, updated];
      return list.filter((x) => x.activa).length;
    });
    setEditingMachine(null);
    load(); // refresh counts in zones
  };

  const openNewMachine = (zona = null) => {
    setEditingMachine({
      ...NEW_MACHINE_TEMPLATE,
      planta: zona ? zona.codigo : null,
      numero: zona ? zona.codigo : "",
    });
  };

  const handleDeleteAll = async () => {
    if (deleteConfirm !== "BORRAR") return;
    setDeleting(true);
    try {
      await machinesApi.deleteAll();
      setShowDeleteAll(false);
      setDeleteConfirm("");
      await load();
    } catch (e) { setImportError("Error al borrar"); }
    finally { setDeleting(false); }
  };

  const handleReoptimizar = async () => {
    if (!window.confirm("¿Re-optimizar todas las fotos existentes? El proceso corre en segundo plano, verás el resultado en unos segundos.")) return;
    setReoptimizing(true); setReoptResult(null);
    try {
      await adminApi.reoptimizarFotos(); // returns immediately
      // Poll every 3s until done
      const poll = async () => {
        const estado = await adminApi.reoptimizarFotosEstado();
        if (estado.status === "done") {
          setReoptResult(estado.result);
          setReoptimizing(false);
        } else if (estado.status === "running") {
          setTimeout(poll, 3000);
        } else {
          setReoptimizing(false);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Error al iniciar"); setReoptimizing(false); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="maquinas-tab">

      {/* Header + view toggle */}
      <div className="flex items-start justify-between mb-7 gap-4">
        <div>
          <h1 className="text-xl font-black tracking-wider">Catálogo de Máquinas</h1>
          <p className="text-xs text-white/40 mt-0.5">
            {machineCount !== null ? `${machineCount} máquina${machineCount !== 1 ? "s" : ""} activas` : "Cargando..."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => openNewMachine()} data-testid="nueva-maquina-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase hover:bg-white transition-colors shrink-0">
            <Plus size={12} /> Nueva máquina
          </button>
          <button onClick={handleReoptimizar} disabled={reoptimizing} data-testid="reoptimizar-fotos-btn"
            title="Comprime todas las fotos existentes a máx. 1200px / JPEG 82%"
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-white/12 text-white/45 font-black text-[10px] tracking-[0.2em] uppercase hover:border-[#FFCC00]/40 hover:text-[#FFCC00] transition-colors shrink-0 disabled:opacity-40">
            {reoptimizing ? <Loader2 size={12} className="animate-spin" /> : <ImageDown size={12} />}
            {reoptimizing ? "Optimizando…" : "Optimizar fotos"}
          </button>
          <button onClick={() => { setShowDeleteAll(true); setDeleteConfirm(""); }} data-testid="delete-all-machines-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-red-500/40 text-red-400 font-black text-[10px] tracking-[0.2em] uppercase hover:bg-red-500/10 transition-colors shrink-0">
            <Trash2 size={12} /> Borrar todo
          </button>
          <div className="flex border border-white/10 shrink-0">
            <button onClick={() => setView("catalog")} data-testid="view-catalog-btn"
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${view === "catalog" ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
              <LayoutGrid size={12} /> Catálogo
            </button>
            <button onClick={() => setView("gestion")} data-testid="view-gestion-btn"
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${view === "gestion" ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
              <List size={12} /> Gestión
            </button>
            <button onClick={() => setView("zonas")} data-testid="view-zonas-btn"
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${view === "zonas" ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
              <MapPin size={12} /> Zonas
            </button>
            <button onClick={() => setView("accesorios")} data-testid="view-accesorios-btn"
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${view === "accesorios" ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
              <Package size={12} /> Accesorios
            </button>
          </div>
        </div>
      </div>

      {/* Reopt result */}
      {reoptResult && (
        <div className="mb-5 flex items-start justify-between gap-3 border border-green-500/20 bg-green-500/6 px-4 py-3" data-testid="reopt-result">
          <p className="text-xs text-green-300 font-bold">
            ✓ {reoptResult.optimizadas} foto{reoptResult.optimizadas !== 1 ? "s" : ""} optimizada{reoptResult.optimizadas !== 1 ? "s" : ""} · Ahorro: {reoptResult.ahorro_total_kb} KB
            {reoptResult.saltadas > 0 && <span className="text-white/35 font-normal"> · {reoptResult.saltadas} ya optimizadas</span>}
            {reoptResult.errores > 0 && <span className="text-red-300"> · {reoptResult.errores} errores</span>}
          </p>
          <button onClick={() => setReoptResult(null)} className="text-white/30 hover:text-white shrink-0"><X size={13} /></button>
        </div>
      )}

      {/* ====== CATALOG VIEW ====== */}
      {view === "catalog" && (
        <div data-testid="catalog-view">
          <div className="flex flex-wrap gap-2 mb-6">
            <div className="relative flex-1 min-w-[180px]">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o número..."
                data-testid="filter-search"
                className="w-full bg-black border border-white/10 text-white placeholder:text-white/25 pl-8 pr-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors" />
              {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"><X size={12} /></button>}
            </div>
            <select value={filterGrupo} onChange={(e) => setFilterGrupo(e.target.value)} data-testid="filter-grupo"
              className="bg-black border border-white/10 text-white/70 px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors appearance-none min-w-[140px]">
              <option value="">Grupo muscular</option>
              {grupos.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select value={filterDif} onChange={(e) => setFilterDif(e.target.value)} data-testid="filter-dificultad"
              className="bg-black border border-white/10 text-white/70 px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors appearance-none min-w-[120px]">
              <option value="">Dificultad</option>
              <option value="1">Fácil</option>
              <option value="2">Medio</option>
              <option value="3">Avanzado</option>
            </select>
            {plantas.length > 1 && (
              <select value={filterPlanta} onChange={(e) => setFilterPlanta(e.target.value)} data-testid="filter-planta"
                className="bg-black border border-white/10 text-white/70 px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors appearance-none min-w-[110px]">
                <option value="">Zona</option>
                {plantas.map((p) => {
                  const z = zones.find((z) => z.codigo === p);
                  return <option key={p} value={p}>{z ? `${p} — ${z.nombre}` : p}</option>;
                })}
              </select>
            )}
            {hasFilters && (
              <button onClick={clearFilters} data-testid="clear-filters-btn"
                className="flex items-center gap-1.5 px-3 py-2.5 border border-white/10 text-white/40 hover:text-white hover:border-white/25 text-xs font-bold uppercase tracking-wider transition-colors">
                <X size={11} /> Limpiar
              </button>
            )}
          </div>
          {hasFilters && <p className="text-[10px] text-white/30 uppercase tracking-widest mb-4">{filtered.length} resultado{filtered.length !== 1 ? "s" : ""}</p>}
          {filtered.length === 0 ? (
            <div className="border border-white/8 p-12 text-center">
              <Dumbbell size={32} className="mx-auto text-white/10 mb-3" />
              <p className="text-white/30 text-sm">{hasFilters ? "Sin resultados para este filtro." : "No hay máquinas todavía."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="machine-grid">
              {filtered.map((m) => (
                <MachineCard key={m.numero} machine={m} zones={zones} onEdit={() => setEditingMachine({ ...m, _existing: true })} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ====== GESTIÓN VIEW ====== */}
      {view === "gestion" && (
        <div data-testid="gestion-view">
          <div className="border border-white/10 bg-[#0a0a0a] p-6 space-y-6 mb-8" data-testid="machines-section">
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase font-bold text-white/55 mb-2">URL pública del gimnasio (para los QR)</label>
              <input type="url" value={qrBaseUrl} onChange={(e) => setQrBaseUrl(e.target.value)} placeholder="https://xprofitness.es" data-testid="qr-base-url"
                className="w-full bg-black border border-white/12 text-white placeholder:text-white/20 px-4 py-3 text-sm focus:outline-none focus:border-[#FFCC00] transition-colors" />
              <p className="text-[10px] text-white/25 mt-1">Los QR apuntarán a <span className="text-white/45">{qrBaseUrl}/maquina/[NUMERO]</span></p>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase font-bold text-white/55 mb-2">Importar / Actualizar Catálogo (CSV / Excel)</label>
              <div className="flex gap-3">
                <label className="flex-1 flex items-center gap-2 bg-black border border-white/12 text-white/45 px-4 py-3 text-sm cursor-pointer hover:border-[#FFCC00] transition-colors min-w-0">
                  <FileUp size={14} className="shrink-0" />
                  <span className="truncate">{importFile ? importFile.name : "Seleccionar CSV o Excel (.xlsx)..."}</span>
                  <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="csv-file-input"
                    onChange={(e) => { setImportFile(e.target.files[0] || null); setImportResult(null); setImportError(""); }} />
                </label>
                <button onClick={onImportCsv} disabled={!importFile || importing} data-testid="csv-import-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#FFCC00] text-black font-bold text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40 shrink-0">
                  {importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {importing ? "..." : "Importar"}
                </button>
              </div>
              <p className="text-[10px] text-white/25 mt-1">Upsert por número — reimportable sin duplicados.</p>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase font-bold text-white/55 mb-2">Hoja de QRs para imprimir (rejilla 2×3)</label>
              <button onClick={onDownloadQr} disabled={qrDownloading || !machineCount} data-testid="qr-download-btn"
                className="inline-flex items-center gap-2 px-5 py-3 border border-white/12 text-white/70 hover:border-[#FFCC00] hover:text-[#FFCC00] font-bold text-[11px] tracking-[0.2em] uppercase transition-colors disabled:opacity-40">
                {qrDownloading ? <Loader2 size={13} className="animate-spin" /> : <QrCode size={13} />}
                {qrDownloading ? "Generando PDF..." : `Descargar PDF QR (${machineCount ?? 0} máquinas)`}
              </button>
            </div>
            {importError && <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="import-error">{importError}</div>}
            {importResult && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 text-green-300 text-sm" data-testid="import-result">
                <strong>{importResult.inserted}</strong> insertadas · <strong>{importResult.updated}</strong> actualizadas · <span className="text-white/45">{importResult.skipped}</span> saltadas ({importResult.total_rows} filas)
              </div>
            )}
          </div>
          {machines.length > 0 && (
            <div data-testid="machines-list">
              <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-white/50 mb-3">Máquinas ({machines.length})</p>
              <div className="border border-white/10 divide-y divide-white/5">
                {machines.map((m) => {
                  const zona = zones.find((z) => z.codigo === m.planta);
                  return (
                    <div key={m.numero} className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors" data-testid={`machine-row-${m.numero}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="bg-[#FFCC00] text-black font-black text-[10px] px-2 py-0.5 tracking-wider shrink-0">{m.numero}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-white truncate">{m.nombre}</p>
                          <p className="text-[10px] text-white/30 truncate">
                            {[m.grupo_muscular, zona ? `${zona.codigo} — ${zona.nombre}` : m.planta && `Zona ${m.planta}`].filter(Boolean).join(" · ") || "Sin clasificar"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <span className={`text-[9px] font-black tracking-widest uppercase px-2 py-0.5 border ${m.activa ? "border-green-500/30 text-green-400" : "border-red-500/20 text-red-400"}`}>
                          {m.activa ? "Activa" : "Inactiva"}
                        </span>
                        <a href={`/maquina/${m.numero}`} target="_blank" rel="noreferrer" className="p-1.5 text-white/25 hover:text-[#FFCC00] transition-colors" data-testid={`machine-view-${m.numero}`}>
                          <ExternalLink size={13} />
                        </a>
                        <button onClick={() => setEditingMachine({ ...m, _existing: true })} data-testid={`machine-edit-${m.numero}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-white/10 text-white/45 hover:border-[#FFCC00]/60 hover:text-[#FFCC00] text-[10px] font-bold tracking-wider uppercase transition-colors">
                          <Pencil size={11} /> Editar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ====== ZONAS VIEW ====== */}
      {view === "zonas" && (
        <ZonasPanel zones={zones} onChanged={load} onAddMachine={(z) => openNewMachine(z)} />
      )}

      {/* ====== ACCESORIOS VIEW ====== */}
      {view === "accesorios" && (
        <AccesoriosPanel />
      )}

      {editingMachine && (
        <MachineEditModal machine={editingMachine} zones={zones} onClose={() => setEditingMachine(null)} onSaved={handleSaved} />
      )}

      {showDeleteAll && (
        <DeleteAllModal
          title="Borrar todas las máquinas"
          description={`Se eliminarán permanentemente las ${machines.length} máquinas. Esta acción no se puede deshacer.`}
          onConfirm={handleDeleteAll}
          onClose={() => setShowDeleteAll(false)}
          confirm={deleteConfirm}
          setConfirm={setDeleteConfirm}
          loading={deleting}
        />
      )}
    </div>
  );
}

function ZonasPanel({ zones, onChanged, onAddMachine }) {
  const [form, setForm] = useState({ codigo: "", nombre: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // { codigo, nombre }
  const [editNombre, setEditNombre] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const handleCreate = async () => {
    if (!form.codigo.trim() || !form.nombre.trim()) { setErr("Código y nombre son obligatorios"); return; }
    setSaving(true); setErr("");
    try {
      await zonasApi.create({ codigo: form.codigo.trim().toUpperCase(), nombre: form.nombre.trim() });
      setForm({ codigo: "", nombre: "" });
      onChanged();
    } catch (e) { setErr(formatApiError(e?.response?.data?.detail) || "Error creando zona"); }
    finally { setSaving(false); }
  };

  const handleEditSave = async () => {
    if (!editNombre.trim()) return;
    setEditSaving(true);
    try {
      await zonasApi.update(editing.codigo, { nombre: editNombre.trim() });
      setEditing(null);
      onChanged();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Error"); }
    finally { setEditSaving(false); }
  };

  const handleDelete = async (z) => {
    if (!window.confirm(`Eliminar zona "${z.codigo} — ${z.nombre}"?\nSolo es posible si no tiene máquinas asignadas.`)) return;
    setDeleting(z.codigo);
    try {
      await zonasApi.delete(z.codigo);
      onChanged();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Error eliminando"); }
    finally { setDeleting(null); }
  };

  return (
    <div data-testid="zonas-panel">
      {/* Create form */}
      <div className="border border-white/10 bg-[#0a0a0a] p-6 mb-6">
        <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/50 mb-4">Nueva zona</p>
        <div className="flex gap-3 flex-wrap">
          <div className="w-24">
            <label className="block text-[10px] uppercase tracking-widest text-white/35 font-bold mb-1.5">Código</label>
            <input value={form.codigo} onChange={(e) => setForm((f) => ({ ...f, codigo: e.target.value.toUpperCase().replace(/[^A-Z]/g, "") }))}
              placeholder="A" maxLength={3} data-testid="zona-codigo-input"
              className="w-full bg-black border border-white/12 text-white font-black text-center text-lg px-3 py-2.5 outline-none focus:border-[#FFCC00] transition-colors tracking-widest" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[10px] uppercase tracking-widest text-white/35 font-bold mb-1.5">Nombre</label>
            <input value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              placeholder="Ej: Sala de Musculación" data-testid="zona-nombre-input"
              className="w-full bg-black border border-white/12 text-white px-4 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors"
              onKeyDown={(e) => e.key === "Enter" && handleCreate()} />
          </div>
          <div className="flex items-end">
            <button onClick={handleCreate} disabled={saving} data-testid="zona-create-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Crear zona
            </button>
          </div>
        </div>
        {err && <p className="text-red-400 text-xs mt-3" data-testid="zona-error">{err}</p>}
      </div>

      {/* Zones list */}
      {zones.length === 0 ? (
        <div className="border border-white/8 border-dashed p-12 text-center">
          <MapPin size={32} className="mx-auto text-white/10 mb-3" />
          <p className="text-white/30 text-sm">No hay zonas definidas.</p>
          <p className="text-white/20 text-xs mt-1">Crea la primera zona arriba para organizar tus máquinas.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="zonas-list">
          {zones.map((z) => (
            <div key={z.codigo} className="border border-white/10 bg-[#0a0a0a] px-5 py-4 flex items-center justify-between gap-4" data-testid={`zona-row-${z.codigo}`}>
              {editing?.codigo === z.codigo ? (
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="bg-[#FFCC00] text-black font-black text-sm px-2.5 py-1 tracking-widest shrink-0">{z.codigo}</span>
                  <input value={editNombre} onChange={(e) => setEditNombre(e.target.value)} autoFocus
                    className="flex-1 bg-black border border-[#FFCC00] text-white px-3 py-1.5 text-sm outline-none"
                    onKeyDown={(e) => { if (e.key === "Enter") handleEditSave(); if (e.key === "Escape") setEditing(null); }} />
                  <button onClick={handleEditSave} disabled={editSaving}
                    className="px-3 py-1.5 bg-[#FFCC00] text-black font-black text-[10px] uppercase tracking-wider hover:bg-white transition-colors disabled:opacity-40">
                    {editSaving ? <Loader2 size={11} className="animate-spin" /> : "OK"}
                  </button>
                  <button onClick={() => setEditing(null)} className="text-white/30 hover:text-white transition-colors"><X size={14} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="bg-[#FFCC00] text-black font-black text-sm px-2.5 py-1 tracking-widest shrink-0">{z.codigo}</span>
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-white">{z.nombre}</p>
                    <p className="text-[11px] text-white/35 mt-0.5">{z.maquinas || 0} máquina{z.maquinas !== 1 ? "s" : ""}</p>
                  </div>
                </div>
              )}
              {editing?.codigo !== z.codigo && (
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => onAddMachine(z)} data-testid={`zona-add-machine-${z.codigo}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-[#FFCC00]/30 text-[#FFCC00]/70 hover:border-[#FFCC00] hover:text-[#FFCC00] text-[10px] font-bold tracking-wider uppercase transition-colors">
                    <Plus size={11} /> Máquina
                  </button>
                  <button onClick={() => { setEditing(z); setEditNombre(z.nombre); }} data-testid={`zona-edit-${z.codigo}`}
                    className="p-2 border border-white/10 text-white/35 hover:border-[#FFCC00]/50 hover:text-[#FFCC00] transition-colors">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => handleDelete(z)} disabled={deleting === z.codigo} data-testid={`zona-delete-${z.codigo}`}
                    className="p-2 border border-white/10 text-white/35 hover:border-red-500/50 hover:text-red-400 transition-colors disabled:opacity-40">
                    {deleting === z.codigo ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MachineCard({ machine: m, zones, onEdit }) {
  const [imgErr, setImgErr] = useState(false);
  const src = fotoSrc(m.foto_url);
  const zona = zones.find((z) => z.codigo === m.planta);

  return (
    <div className="group relative bg-[#0a0a0a] border border-white/8 hover:border-[#FFCC00]/40 transition-all duration-200 overflow-hidden flex flex-col"
      data-testid={`machine-card-${m.numero}`}>
      <div className="relative aspect-square bg-black overflow-hidden">
        {src && !imgErr ? (
          <img src={src} alt={m.nombre} onError={() => setImgErr(true)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-white/[0.02]">
            <Dumbbell size={28} className="text-white/10" />
          </div>
        )}
        <span className="absolute top-2 left-2 bg-[#FFCC00] text-black font-black text-[10px] px-1.5 py-0.5 tracking-wider leading-none">{m.numero}</span>
        {!m.activa && (
          <span className="absolute top-2 right-2 bg-black/80 border border-red-500/40 text-red-400 text-[9px] font-black px-1.5 py-0.5 tracking-widest uppercase">Inactiva</span>
        )}
        <button onClick={onEdit} data-testid={`card-edit-${m.numero}`}
          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <span className="bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 flex items-center gap-1">
            <Pencil size={10} /> Editar
          </span>
        </button>
      </div>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <p className="text-xs font-black text-white leading-tight line-clamp-2">{m.nombre}</p>
        {m.grupo_muscular && <p className="text-[10px] text-[#FFCC00]/70 font-bold uppercase tracking-wider truncate">{m.grupo_muscular}</p>}
        <div className="flex items-center justify-between mt-auto pt-1">
          {m.dificultad != null ? (
            <span className={`text-[9px] font-black tracking-wider uppercase px-1.5 py-0.5 border ${DIFICULTAD_COLOR[m.dificultad] || "text-white/30 border-white/10"}`}>
              {DIFICULTAD_LABEL[m.dificultad] || `Niv. ${m.dificultad}`}
            </span>
          ) : <span />}
          {m.planta && (
            <span className="text-[9px] text-white/30 uppercase tracking-wider font-bold">
              {zona ? `${zona.codigo}` : m.planta}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function DeleteAllModal({ title, description, onConfirm, onClose, confirm, setConfirm, loading }) {
  const ready = confirm === "BORRAR";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="delete-all-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-[#0d0d0d] border border-red-500/30 shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-red-500/10 border border-red-500/30 flex items-center justify-center shrink-0">
            <Trash2 size={16} className="text-red-400" />
          </div>
          <h3 className="font-black text-sm tracking-wider text-white uppercase">{title}</h3>
        </div>
        <p className="text-white/50 text-xs leading-relaxed mb-5">{description}</p>
        <div className="mb-5">
          <label className="block text-[10px] uppercase tracking-widest text-white/35 font-bold mb-2">
            Escribe <span className="text-red-400 font-black">BORRAR</span> para confirmar
          </label>
          <input
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="BORRAR" autoFocus data-testid="delete-confirm-input"
            className="w-full bg-black border border-white/12 text-white px-3 py-2.5 text-sm outline-none focus:border-red-500 transition-colors tracking-widest font-bold"
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onConfirm} disabled={!ready || loading} data-testid="confirm-delete-all-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white font-black text-[10px] tracking-[0.2em] uppercase py-3 hover:bg-red-500 transition-colors disabled:opacity-30">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {loading ? "Borrando..." : "Confirmar borrado"}
          </button>
          <button onClick={onClose} className="px-4 border border-white/12 text-white/40 text-[10px] font-bold uppercase hover:text-white transition-colors">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}


const TIPOS_ACCESORIO = ["Agarre", "Barra", "Cable / Polea", "Mancuerna", "Kettlebell", "Banda elástica", "Peso libre", "Otro"];
const TIPO_COLOR = {
  "Agarre": "text-[#FFCC00] border-[#FFCC00]/30",
  "Barra": "text-blue-400 border-blue-500/30",
  "Cable / Polea": "text-purple-400 border-purple-500/30",
  "Mancuerna": "text-green-400 border-green-500/30",
  "Kettlebell": "text-orange-400 border-orange-500/30",
  "Banda elástica": "text-pink-400 border-pink-500/30",
  "Peso libre": "text-cyan-400 border-cyan-500/30",
  "Otro": "text-white/40 border-white/15",
};

const EMPTY_ACC = { nombre: "", tipo: "Agarre", descripcion: "", activo: true };

function AccesoriosPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | item (with _isNew)
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try { setItems(await accesoriosApi.adminList()); }
    catch { } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY_ACC, _isNew: true });
  const openEdit = (a) => setEditing({ ...a, _isNew: false });

  const handleSave = async () => {
    if (!editing.nombre?.trim()) { setErr("El nombre es obligatorio"); return; }
    setSaving(true); setErr("");
    try {
      const payload = { nombre: editing.nombre.trim(), tipo: editing.tipo, descripcion: editing.descripcion || null, activo: editing.activo };
      if (editing._isNew) {
        const created = await accesoriosApi.create(payload);
        setItems(prev => [...prev, created]);
      } else {
        const updated = await accesoriosApi.update(editing.id, payload);
        setItems(prev => prev.map(a => a.id === editing.id ? updated : a));
      }
      setEditing(null);
    } catch (e) { setErr(e?.response?.data?.detail || "Error guardando"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este accesorio? Se desvinculará de todos los ejercicios asociados.")) return;
    await accesoriosApi.delete(id);
    setItems(prev => prev.filter(a => a.id !== id));
  };

  const byTipo = TIPOS_ACCESORIO.map(t => ({ tipo: t, items: items.filter(a => a.tipo === t) })).filter(g => g.items.length > 0);
  const sinTipo = items.filter(a => !TIPOS_ACCESORIO.includes(a.tipo));

  return (
    <div data-testid="accesorios-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs text-white/40">{items.length} accesorio{items.length !== 1 ? "s" : ""} en total</p>
        </div>
        <button onClick={openNew} data-testid="nuevo-accesorio-btn"
          className="flex items-center gap-1.5 px-4 py-2.5 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase hover:bg-white transition-colors">
          <Plus size={12} /> Nuevo accesorio
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#FFCC00]" /></div>
      ) : items.length === 0 ? (
        <div className="border border-white/8 border-dashed p-16 text-center">
          <Package size={40} className="mx-auto text-white/10 mb-4" />
          <p className="text-white/30 text-sm">Todavía no hay accesorios.</p>
          <p className="text-white/20 text-xs mt-1">Crea agarres, barras y otros accesorios para asociarlos a los ejercicios.</p>
          <button onClick={openNew} className="mt-4 px-4 py-2 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase hover:bg-white transition-colors">
            + Crear primer accesorio
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {byTipo.map(({ tipo, items: group }) => (
            <div key={tipo}>
              <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] mb-3 ${(TIPO_COLOR[tipo] || "text-white/40").split(" ")[0]}`}>{tipo}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {group.map(a => (
                  <div key={a.id} data-testid={`acc-card-${a.id}`}
                    className={`border bg-white/[0.02] p-4 flex items-start justify-between gap-2 ${a.activo ? "border-white/8" : "border-white/4 opacity-50"}`}>
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-white truncate">{a.nombre}</p>
                      {a.descripcion && <p className="text-[10px] text-white/35 mt-0.5 line-clamp-2">{a.descripcion}</p>}
                      {!a.activo && <p className="text-[9px] text-white/25 uppercase tracking-wider mt-1">Inactivo</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => openEdit(a)} className="p-1.5 border border-white/10 text-white/40 hover:text-[#FFCC00] hover:border-[#FFCC00]/30 transition-colors">
                        <Pencil size={11} />
                      </button>
                      <button onClick={() => handleDelete(a.id)} className="p-1.5 border border-white/10 text-white/40 hover:text-red-400 hover:border-red-500/30 transition-colors">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {sinTipo.length > 0 && (
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 text-white/40">Otros</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {sinTipo.map(a => (
                  <div key={a.id} className="border border-white/8 bg-white/[0.02] p-4 flex items-start justify-between gap-2">
                    <p className="font-bold text-sm text-white truncate">{a.nombre}</p>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => openEdit(a)} className="p-1.5 border border-white/10 text-white/40 hover:text-[#FFCC00] transition-colors"><Pencil size={11} /></button>
                      <button onClick={() => handleDelete(a.id)} className="p-1.5 border border-white/10 text-white/40 hover:text-red-400 transition-colors"><Trash2 size={11} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit / Create modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-xl bg-[#0d0d0d] border border-white/10 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-sm tracking-wider uppercase">{editing._isNew ? "Nuevo accesorio" : "Editar accesorio"}</h3>
              <button onClick={() => setEditing(null)} className="text-white/30 hover:text-white"><X size={16} /></button>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-white/35 mb-1.5">Nombre *</label>
              <input value={editing.nombre} onChange={e => setEditing(p => ({...p, nombre: e.target.value}))}
                data-testid="acc-nombre" placeholder="Ej: Agarre de cuerda doble"
                className="w-full bg-black border border-white/12 text-white px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors" />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-white/35 mb-1.5">Tipo</label>
              <select value={editing.tipo} onChange={e => setEditing(p => ({...p, tipo: e.target.value}))}
                data-testid="acc-tipo"
                className="w-full bg-black border border-white/12 text-white px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors appearance-none">
                {TIPOS_ACCESORIO.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-white/35 mb-1.5">Descripción (opcional)</label>
              <textarea value={editing.descripcion || ""} onChange={e => setEditing(p => ({...p, descripcion: e.target.value}))}
                data-testid="acc-descripcion" rows={2} placeholder="Descripción breve del accesorio..."
                className="w-full bg-black border border-white/12 text-white px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors resize-none" />
            </div>

            <div>
              <button type="button" onClick={() => setEditing(p => ({...p, activo: !p.activo}))}
                className={`w-full py-2 text-[10px] font-black tracking-widest uppercase border transition-colors ${editing.activo ? "bg-white/5 border-white/15 text-white/70" : "bg-white/2 border-white/8 text-white/30"}`}>
                {editing.activo ? "Activo — visible en ejercicios" : "Inactivo — no aparece al crear ejercicios"}
              </button>
            </div>

            {err && <p className="text-red-400 text-xs">{err}</p>}

            <div className="flex gap-3 pt-1">
              <button onClick={handleSave} disabled={saving} data-testid="acc-save-btn"
                className="flex-1 flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase py-3 hover:bg-white transition-colors disabled:opacity-50">
                {saving ? <Loader2 size={13} className="animate-spin" /> : null}
                {saving ? "Guardando..." : "Guardar"}
              </button>
              <button onClick={() => setEditing(null)} className="px-4 border border-white/12 text-white/40 text-[10px] font-bold uppercase hover:text-white transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
